import { TableNode } from "@lexical/table";
import {
	DOMConversion,
	DOMConversionMap,
	DOMConversionOutput,
	LexicalNode,
	NodeKey,
} from "lexical";

export class ExtendedTableNode extends TableNode {
	constructor(key?: NodeKey) {
		super(key);	
	}

	static override getType(): string {
		return "extended-table";
	}

	static override clone(node: ExtendedTableNode): ExtendedTableNode {
		return new ExtendedTableNode(node.__key);
	}

	static override importDOM(): DOMConversionMap | null {
		const importers  = TableNode.importDOM();
		return {
			...importers ,
			table: () => ({
				conversion: patchStyleConversion(importers?.["table"]),
				priority: 1,
			}),
		};
	}
}

export function $isTableNode(
	node: LexicalNode | null | undefined
): node is ExtendedTableNode {
	return node instanceof ExtendedTableNode;
}

function patchStyleConversion(
	originalDOMConverter?: (node: HTMLElement) => DOMConversion | null
): (node: HTMLElement) => DOMConversionOutput | null {
	return (node) => {
		const original = originalDOMConverter?.(node);
		if (!original) {
			return null;
		}
		const originalOutput = original.conversion(node);

		if (!originalOutput) {
			return originalOutput;
		}

		const backgroundColor = node.style.backgroundColor;
		const color = node.style.color;
		const fontFamily = node.style.fontFamily;
		const fontWeight = node.style.fontWeight;
		const fontSize = node.style.fontSize;
		const textDecoration = node.style.textDecoration;
		const border = node.style.border;
		const borderCollapse = node.style.borderCollapse;

		if (
			!Array.isArray(originalOutput.node) &&
			$isTableNode(originalOutput.node)
		) {
			const style = [
				backgroundColor ? `background-color: ${backgroundColor}` : null,
				color ? `color: ${color}` : null,
				fontFamily ? `font-family: ${fontFamily}` : null,
				fontWeight ? `font-weight: ${fontWeight}` : null,
				fontSize ? `font-size: ${fontSize}` : null,
				textDecoration ? `text-decoration: ${textDecoration}` : null,
				border ? `border: ${border}` : null,
				borderCollapse ? `borderCollapse: ${borderCollapse}` : null,
			]
				.filter((value) => value != null)
				.join("; ");

			originalOutput.node.setStyle(style);
		}

		return {
			...originalOutput,
			forChild: (lexicalNode, parent) => {
				const originalForChild = originalOutput?.forChild ?? ((x) => x);
				const result = originalForChild(lexicalNode, parent);
				if ($isTableNode(result)) {
					const style = [
						backgroundColor ? `background-color: ${backgroundColor}` : null,
						color ? `color: ${color}` : null,
						fontFamily ? `font-family: ${fontFamily}` : null,
						fontWeight ? `font-weight: ${fontWeight}` : null,
						fontSize ? `font-size: ${fontSize}` : null,
						textDecoration ? `text-decoration: ${textDecoration}` : null,
						border ? `border: ${border}` : null,
					]
						.filter((value) => value != null)
						.join("; ");
					if (style.length) {
						return result.setStyle(style);
					}
				}
				return result;
			},
		};
	};
}
